import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState, useRef, useId } from 'react';
import cx from 'clsx';
import { Label } from '../form-elements';
import { fadeIn, fadeOut, translate } from './animations';
import { ChoiceChip, type ChoiceChipSize, type ChoiceChipVariant } from './choice-chip';
import styles from './choice-chips.module.css';

export interface ChoiceChipOption<T> {
  value: T;
  label: ReactNode;
  isDisabled?: boolean;
}

interface BaseChoiceChipProps<T> {
  label?: string;
  errorMessage?: string;
  options: ChoiceChipOption<T>[];
  size?: ChoiceChipSize;
  variant?: ChoiceChipVariant;
  allowWrapping?: boolean;
  isDisabled?: boolean;
  isOptional?: boolean;
  className?: string;
}

interface ToggleChoiceChipProps<T> {
  selectionBehavior?: 'replace';
  onChange?: (value: T) => void;
}

interface ReplaceChoiceChipProps<T> {
  selectionBehavior: 'toggle';
  onChange?: (value?: T) => void;
}

type SelectionBehaviorChoiceChipProps<T> = ToggleChoiceChipProps<T> | ReplaceChoiceChipProps<T>;

interface BaseSingleChoiceChipProps<T> {
  selectionMode?: 'single';
  defaultValue?: T | undefined;
  value?: T | undefined;
  defaultValues?: never;
  values?: never;
  customOption?: {
    label: string;
    input: ReactNode;
  };
}

type SingleChoiceChipProps<T> = BaseSingleChoiceChipProps<T> & SelectionBehaviorChoiceChipProps<T>;

interface MultipleChoiceChipProps<T> {
  selectionMode: 'multiple';
  selectionBehavior?: never;
  defaultValue?: never;
  value?: never;
  defaultValues?: T[] | undefined;
  values?: T[] | undefined;
  customOption?: never;
  onChange?: (value: T[]) => void;
}

type SingleOrMultipleChipProps<T> = SingleChoiceChipProps<T> | MultipleChoiceChipProps<T>;

type ChoiceChipProps<T> = BaseChoiceChipProps<T> & SingleOrMultipleChipProps<T>;

function toArray<T>(value: T | T[] | undefined): T[] {
  if (value === undefined) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
}

export function ChoiceChips<T>({
  label,
  errorMessage,
  size,
  variant,
  options,
  selectionMode,
  selectionBehavior,
  defaultValue,
  value,
  defaultValues = [],
  values,
  onChange,
  customOption,
  allowWrapping,
  isDisabled,
  isOptional,
  ...props
}: ChoiceChipProps<T>): ReactElement {
  const uuid = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedValues, setSelectedValues] = useState(
    toArray(selectionMode === 'multiple' ? (values ?? defaultValues) : (value ?? defaultValue))
  );

  const hasCustomInput = Boolean(customOption);
  const [showCustomInput, setShowCustomInput] = useState<boolean>(
    selectionMode !== 'multiple' &&
      defaultValue !== undefined &&
      hasCustomInput &&
      options.every(option => option.value !== defaultValue)
  );
  const wrap = allowWrapping && !showCustomInput;

  useEffect(() => {
    if (selectionMode !== 'multiple' && value !== undefined) {
      setSelectedValues(toArray(value));

      if (hasCustomInput) {
        setShowCustomInput(options.every(option => option.value !== value));
      }
    }
  }, [hasCustomInput, selectionMode, options, value]);

  useEffect(() => {
    if (selectionMode === 'multiple' && values) {
      setSelectedValues(values);
    }
  }, [selectionMode, values]);

  function isSelected(optionValue: T): boolean {
    return selectedValues.includes(optionValue);
  }

  function handleChange(optionValue: T): void {
    if (selectionMode === 'multiple') {
      if (isSelected(optionValue)) {
        setSelectedValues(currentSelectedValues => {
          const index = currentSelectedValues.indexOf(optionValue);

          if (index !== -1) {
            currentSelectedValues.splice(index, 1);
            const newSelectedValues = [...currentSelectedValues];
            onChange?.(newSelectedValues);
            return newSelectedValues;
          }

          return currentSelectedValues;
        });
      } else {
        setSelectedValues(currentSelectedValues => {
          const newSelectedValues = [...currentSelectedValues];
          newSelectedValues.push(optionValue);
          onChange?.(newSelectedValues);
          return [...newSelectedValues];
        });
      }
    } else if (isSelected(optionValue) && selectionBehavior === 'toggle') {
      setSelectedValues([]);
      onChange?.();
    } else {
      setSelectedValues([optionValue]);
      onChange?.(optionValue);
    }
  }

  const toggleCustomOption = () => (): void => {
    const element = containerRef.current;
    if (!element || !hasCustomInput) return;

    if (showCustomInput) {
      setShowCustomInput(false);
      return;
    }

    setShowCustomInput(true);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!reduceMotion.matches) {
      const optionElements: HTMLElement[] = Array.from(
        element.querySelectorAll('[data-chip-option]')
      );
      const customOptionElement: HTMLElement | null = element.querySelector(
        '[data-chip-custom-option]'
      );
      const collapsedContentElement: HTMLElement | null = element.querySelector(
        '[data-chip-custom-input]'
      );

      const startPositions = optionElements.map(optionElement => optionElement.offsetLeft);
      const customOptionStartPosition = customOptionElement?.offsetLeft;

      void Promise.all([
        ...optionElements.map((optionElement, i) => {
          const startPosition = startPositions[i];
          return fadeOut(optionElement, startPosition);
        }),
        customOptionElement && translate(customOptionElement, customOptionStartPosition),
        collapsedContentElement && fadeIn(collapsedContentElement),
      ]);
    }
  };

  return (
    <div {...props}>
      {label ? (
        <div className={styles.label}>
          <Label id={uuid} isOptional={isOptional}>
            {label}
          </Label>
        </div>
      ) : null}
      <div
        aria-disabled={isDisabled}
        aria-labelledby={uuid}
        className={cx(styles.wrapper, wrap && styles.wrapperWrap)}
        ref={containerRef}
        role="listbox"
      >
        {options.map(option => (
          <ChoiceChip
            isChecked={isSelected(option.value)}
            isCollapsed={showCustomInput}
            isDisabled={isDisabled || option.isDisabled}
            key={String(option.value)}
            onChange={handleChange}
            size={size}
            value={option.value}
            variant={variant}
          >
            {option.label}
          </ChoiceChip>
        ))}
        {customOption ? (
          <>
            <ChoiceChip
              data-chip-custom-option=""
              isChecked={showCustomInput}
              isClearable
              isDisabled={isDisabled}
              onChange={toggleCustomOption()}
              size={size}
              value={null}
              variant={variant}
            >
              {customOption.label}
            </ChoiceChip>
            <div
              className={cx(styles.content, showCustomInput && styles.contentVisible)}
              data-chip-custom-input=""
            >
              {customOption.input}
            </div>
          </>
        ) : null}
      </div>
      {errorMessage ? (
        <div className={styles.error} slot="errorMessage">
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
}
